import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Select,
  MenuItem,
} from "@mui/material";
import { palette } from "../../components/common/Palette";
import MyButton from "../../new-components/common/MyButton";

import {
  useSearchServiceAreasLocality,
  ServiceAreaLocalityItem,
  ServiceAreaLocalityApiResponse,
} from "../../api-data/service-provider/service-area-locality";
import { ServiceAreaItem } from "../../api-data/service-provider/service-areas";

export interface LocationModalContentProps {
  onApply: (
    city: string,
    locality: string,
    cityId?: number,
    localityId?: number,
  ) => void;
  cities: ServiceAreaItem[];
  initialCity?: string;
  initialLocality?: string;
  resetKey?: number;
}

interface LocationModalProps extends LocationModalContentProps {
  open: boolean;
  onClose: () => void;
}

export const LocationModalContent: React.FC<LocationModalContentProps> = ({
  onApply,
  cities,
  initialCity = "",
  initialLocality = "",
  resetKey,
}) => {
  const { t } = useTranslation();
  const [selectedCity, setSelectedCity] = useState<string>(initialCity);
  const [selectedLocality, setSelectedLocality] =
    useState<string>(initialLocality);

  useEffect(() => {
    setSelectedCity(initialCity);
    setSelectedLocality(initialLocality);
  }, [resetKey, initialCity, initialLocality]);

  const selectedCityObj = useMemo<ServiceAreaItem | undefined>(
    () => cities.find((c) => c.city === selectedCity),
    [selectedCity, cities],
  );

  const { data: localityResponse } = useSearchServiceAreasLocality(
    100,
    0,
    selectedCityObj?.id,
    "locality,asc",
  );

  const localities = useMemo<ServiceAreaLocalityItem[]>(
    () =>
      localityResponse?.pages?.flatMap(
        (p: ServiceAreaLocalityApiResponse) => p.data,
      ) || [],
    [localityResponse],
  );

  const handleApply = () => {
    const cityObj = cities.find((c) => c.city === selectedCity);
    const localityObj = localities.find((l) => l.locality === selectedLocality);
    onApply(selectedCity, selectedLocality, cityObj?.id, localityObj?.id);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          justifyContent: "center",
          alignItems: "center",
          mb: 2,
          position: "relative",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: palette.text.secondary,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 14,
          }}
        >
          {t("location.title")}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{ mb: 1, color: palette.text.secondary, fontWeight: 500 }}
      >
        {t("location.city")}
      </Typography>
      <Select
        fullWidth
        displayEmpty
        value={selectedCity}
        onChange={(e) => {
          setSelectedCity(e.target.value);
          setSelectedLocality("");
        }}
        sx={{
          mb: 2,
          borderRadius: "20px",
          height: 40,
          fontSize: 13,
          "& .MuiOutlinedInput-root": {
            borderRadius: "20px",
          },
        }}
        renderValue={(value) => {
          if (!value) return t("location.selectCity");
          const cityObj = cities.find((c) => c.city === value);
          // Show translated city name if available, fall back to English
          return cityObj?.localized?.city ?? cityObj?.city ?? value;
        }}
      >
        {cities.map((city) => (
          // value is always English (used for context/search); label shows translated
          <MenuItem key={city.id} value={city.city}>
            {city.localized?.city ?? city.city}
          </MenuItem>
        ))}
      </Select>

      {/* Locality Dropdown */}
      {selectedCityObj?.hasLocality && (
        <>
          <Typography
            variant="body2"
            sx={{ mb: 0.5, color: "text.secondary", fontWeight: 500 }}
          >
            {t("location.area")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 1, fontSize: 13, color: "text.secondary" }}
          >
            {t("location.areaHint")}
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={selectedLocality}
            onChange={(e) => setSelectedLocality(e.target.value)}
            sx={{
              mb: 1,
              borderRadius: "20px",
              height: 40,
              fontSize: 13,
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
              },
            }}
            renderValue={(value) => {
              if (!value) return t("location.selectArea");
              const localityObj = localities.find((l) => l.locality === value);
              return localityObj?.localized?.locality ?? value;
            }}
          >
            {localities.map((locality) => (
              // value is always English; label shows translated
              <MenuItem key={locality.id} value={locality.locality}>
                {locality.localized?.locality ?? locality.locality}
              </MenuItem>
            ))}
          </Select>
        </>
      )}

      {/* Apply Button */}
      <MyButton
        variant="contained"
        fullWidth
        onClick={handleApply}
        disabled={!selectedCity}
        color="primary"
        sx={{
          borderRadius: "22px",
          py: 1,
          fontWeight: 500,
          fontSize: 14,
          textTransform: "none",
          mt: 1,
        }}
      >
        {t("location.applyLocation")}
      </MyButton>
    </Box>
  );
};

const LocationModal: React.FC<LocationModalProps> = ({
  open,
  onClose,
  ...contentProps
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      slotProps={{
        paper: {
          sx: {
            borderRadius: "24px",
            width: { xs: "calc(100% - 8px)", sm: "550px" },
            maxWidth: 600,
            p: 0,
            overflow: "hidden",
          },
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <LocationModalContent {...contentProps} resetKey={open ? 1 : 0} />
      </DialogContent>
    </Dialog>
  );
};

export default LocationModal;
